import { useEffect, useState } from 'react';
import { staffApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CalendarCheck2 } from 'lucide-react';

const RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

export default function ActivationsCard() {
  const [range, setRange] = useState('today');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const load = () => {
    const params = { range };
    if (range === 'custom') {
      if (!from || !to) return;
      params.from = from;
      params.to = to;
    }
    staffApi.get('/dashboard/activations', { params }).then(({ data }) => setData(data));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, from, to]);

  return (
    <div className="bg-white rounded-xl border border-border/40 p-4" data-testid="activations-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarCheck2 size={16} className="text-saffron" />
          <p className="text-xs font-medium text-muted-ink uppercase tracking-wide">Subscriptions Activated</p>
        </div>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="activations-range-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RANGE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {range === 'custom' && (
        <div className="flex items-center gap-2 mt-3">
          <Input type="date" data-testid="activations-from-input" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 text-xs" />
          <Input type="date" data-testid="activations-to-input" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 text-xs" />
        </div>
      )}

      <p className="font-heading text-3xl font-700 text-charcoal mt-3" data-testid="activations-total">
        {data ? data.total : <span className="inline-block h-8 w-10 bg-bone rounded animate-pulse" data-testid="activations-total-skeleton" />}
      </p>

      <Button
        variant="outline"
        size="sm"
        data-testid="activations-view-details-btn"
        onClick={() => setShowDetail((s) => !s)}
        className="mt-2"
      >
        {showDetail ? 'Hide Details' : 'View Details'}
      </Button>

      {showDetail && data && (
        <div className="mt-4 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-bone/60">
                <TableHead>Activated</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Plan Price</TableHead>
                <TableHead>Add-ons</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Final Amount</TableHead>
                <TableHead>Payment Method</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Staff</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.activations.map((a) => (
                <TableRow key={a.id} data-testid="activation-row">
                  <TableCell>{a.activatedAt ? new Date(a.activatedAt).toLocaleString('en-CA') : '—'}</TableCell>
                  <TableCell>{a.customer.name}</TableCell>
                  <TableCell>{a.customer.phone}</TableCell>
                  <TableCell>{a.planNameSnapshot}</TableCell>
                  <TableCell>${Number(a.planPriceSnapshot).toFixed(2)}</TableCell>
                  <TableCell>{a.subscriptionItems.map((it) => it.itemName).join(', ') || '—'}</TableCell>
                  <TableCell>{a.discountCodeRef ? `${a.discountCodeRef.code} (-$${Number(a.discountAmount).toFixed(2)})` : '—'}</TableCell>
                  <TableCell>${Number(a.finalAmount).toFixed(2)}</TableCell>
                  <TableCell>{a.payments[0]?.method || '—'}</TableCell>
                  <TableCell>{a.activationSource === 'ONLINE_PAYMENT' ? 'System / Online' : a.activationSource || '—'}</TableCell>
                  <TableCell>{a.activationSource === 'ONLINE_PAYMENT' ? 'System / Online' : a.activatedByStaff?.name || '—'}</TableCell>
                </TableRow>
              ))}
              {data.activations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-muted-ink py-6">No activations in this range.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
